import React, { useEffect, useState } from 'react';
import ModalLayout from '../ModalLayout';
import image from '../../../assets/yandex.svg';
import { updateDevice } from '../../../api/devices';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/solid';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

// Device types constants
const DEVICE_TYPES = {
  LIGHT: 'devices.types.light',
  LIGHT_STRIP: 'devices.types.light.strip',
  LIGHT_CEILING: 'devices.types.light.ceiling',
  SOCKET: 'devices.types.socket',
  SWITCH: 'devices.types.switch',
  THERMOSTAT: 'devices.types.thermostat',
  THERMOSTAT_AC: 'devices.types.thermostat.ac',
  MEDIA_DEVICE: 'devices.types.media_device',
  MEDIA_DEVICE_TV: 'devices.types.media_device.tv',
  MEDIA_DEVICE_TV_BOX: 'devices.types.media_device.tv_box',
  MEDIA_DEVICE_RECIEVER: 'devices.types.media_device.receiver',
  COOKING: 'devices.types.cooking',
  COFFEE_MAKER: 'devices.types.cooking.coffee_maker',
  KETTLE: 'devices.types.cooking.kettle',
  MULTICOOKER: 'devices.types.cooking.multicooker',
  OPENABLE: 'devices.types.openable',
  OPENABLE_CURTAIN: 'devices.types.openable.curtain',
  OPENABLE_VALVE: 'devices.types.openable.valve',
  HUMIDIFIER: 'devices.types.humidifier',
  PURIFIER: 'devices.types.purifier',
  VACUUM_CLEANER: 'devices.types.vacuum_cleaner',
  WASHING_MACHINE: 'devices.types.washing_machine',
  DISHWASHER: 'devices.types.dishwasher',
  IRON: 'devices.types.iron',
  SENSOR: 'devices.types.sensor',
  SENSOR_MOTION: 'devices.types.sensor.motion',
  SENSOR_VIBRATION: 'devices.types.sensor.vibration',
  SENSOR_ILLUMINATION: 'devices.types.sensor.illumination',
  SENSOR_OPEN: 'devices.types.sensor.open',
  SENSOR_CLIMATE: 'devices.types.sensor.climate',
  SENSOR_WATER_LEAK: 'devices.types.sensor.water_leak',
  SENSOR_BUTTON: 'devices.types.sensor.button',
  SENSOR_GAS: 'devices.types.sensor.gas',
  SENSOR_SMOKE: 'devices.types.sensor.smoke',
  SMART_METER: 'devices.types.smart_meter',
  SMART_METER_COLD_WATER: 'devices.types.smart_meter.cold_water',
  SMART_METER_ELECTRICITY: 'devices.types.smart_meter.electricity',
  SMART_METER_GAS: 'devices.types.smart_meter.gas',
  SMART_METER_HEAT: 'devices.types.smart_meter.heat',
  SMART_METER_HOT_WATER: 'devices.types.smart_meter.hot_water',
  PET_DRINKING_FOUNTAIN: 'devices.types.pet_drinking_fountain',
  PET_FEEDER: 'devices.types.pet_feeder',
  VENTILATION: 'devices.types.ventilation',
  VENTILATION_FAN: 'devices.types.ventilation.fan',
  OTHER: 'devices.types.other',
};

// Device type translations
const DEVICE_TYPE_TRANSLATIONS = {
  [DEVICE_TYPES.LIGHT]: 'Лампа',
  [DEVICE_TYPES.LIGHT_STRIP]: 'Световая лента',
  [DEVICE_TYPES.LIGHT_CEILING]: 'Потолочный светильник',
  [DEVICE_TYPES.SOCKET]: 'Розетка',
  [DEVICE_TYPES.SWITCH]: 'Выключатель',
  [DEVICE_TYPES.THERMOSTAT]: 'Термостат',
  [DEVICE_TYPES.THERMOSTAT_AC]: 'Кондиционер',
  [DEVICE_TYPES.MEDIA_DEVICE]: 'Медиаустройство',
  [DEVICE_TYPES.MEDIA_DEVICE_TV]: 'Телевизор',
  [DEVICE_TYPES.MEDIA_DEVICE_TV_BOX]: 'ТВ-приставка',
  [DEVICE_TYPES.MEDIA_DEVICE_RECIEVER]: 'Рекордер',
  [DEVICE_TYPES.COOKING]: 'Кухонная техника',
  [DEVICE_TYPES.COFFEE_MAKER]: 'Кофемашина',
  [DEVICE_TYPES.KETTLE]: 'Чайник',
  [DEVICE_TYPES.MULTICOOKER]: 'Мультиварка',
  [DEVICE_TYPES.OPENABLE]: 'Открывающееся устройство',
  [DEVICE_TYPES.OPENABLE_CURTAIN]: 'Шторы',
  [DEVICE_TYPES.OPENABLE_VALVE]: 'Клапан',
  [DEVICE_TYPES.HUMIDIFIER]: 'Увлажнитель',
  [DEVICE_TYPES.PURIFIER]: 'Очиститель',
  [DEVICE_TYPES.VACUUM_CLEANER]: 'Пылесос',
  [DEVICE_TYPES.WASHING_MACHINE]: 'Стиральная машина',
  [DEVICE_TYPES.DISHWASHER]: 'Посудомоечная машина',
  [DEVICE_TYPES.IRON]: 'Утюг',
  [DEVICE_TYPES.SENSOR]: 'Датчик',
  [DEVICE_TYPES.SENSOR_MOTION]: 'Датчик движения',
  [DEVICE_TYPES.SENSOR_VIBRATION]: 'Датчик вибрации',
  [DEVICE_TYPES.SENSOR_ILLUMINATION]: 'Датчик освещенности',
  [DEVICE_TYPES.SENSOR_OPEN]: 'Датчик открытия',
  [DEVICE_TYPES.SENSOR_CLIMATE]: 'Датчик климата',
  [DEVICE_TYPES.SENSOR_WATER_LEAK]: 'Датчик протечки воды',
  [DEVICE_TYPES.SENSOR_BUTTON]: 'Датчик кнопки',
  [DEVICE_TYPES.SENSOR_GAS]: 'Датчик газа',
  [DEVICE_TYPES.SENSOR_SMOKE]: 'Датчик дыма',
  [DEVICE_TYPES.SMART_METER]: 'Умный счетчик',
  [DEVICE_TYPES.SMART_METER_COLD_WATER]: 'Счетчик холодной воды',
  [DEVICE_TYPES.SMART_METER_ELECTRICITY]: 'Счетчик электроэнергии',
  [DEVICE_TYPES.SMART_METER_GAS]: 'Счетчик газа',
  [DEVICE_TYPES.SMART_METER_HEAT]: 'Счетчик тепла',
  [DEVICE_TYPES.SMART_METER_HOT_WATER]: 'Счетчик горячей воды',
  [DEVICE_TYPES.PET_DRINKING_FOUNTAIN]: 'Поилка для животных',
  [DEVICE_TYPES.PET_FEEDER]: 'Кормушка для животных',
  [DEVICE_TYPES.VENTILATION]: 'Вентиляция',
  [DEVICE_TYPES.VENTILATION_FAN]: 'Вентилятор',
  [DEVICE_TYPES.OTHER]: 'Другое устройство',
};

// Capability types
const CAPABILITY_TYPES = {
  RANGE: 'devices.capabilities.range',
  ON_OFF: 'devices.capabilities.on_off',
  COLOR_SETTING: 'devices.capabilities.color_setting',
  MODE: 'devices.capabilities.mode',
  TOGGLE: 'devices.capabilities.toggle',
  VIDEO_STREAM: 'devices.capabilities.video_stream',
};

// Компонент для отображения строки таблицы
const FieldRow = ({
  element,
  index,
  onEdit,
  onDelete,
  settingsTranslations,
  availableUnits,
}) => {
  const hasRange = element.range;
  const hasSetting = element.setting;

  return (
    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-center gap-4 border-b border-gray-200 py-3 px-2 hover:bg-gray-50 transition-colors">
      <div className="font-medium text-gray-900">{element.field}</div>
      <div className="text-gray-700">
        {hasRange
          ? settingsTranslations[element.range.instance] ||
            element.range.instance
          : settingsTranslations[element.setting.instance] ||
            element.setting.instance}
      </div>
      <div className="text-gray-600">
        {hasRange
          ? availableUnits[element.range.instance]?.find(
              (unit) => unit.value === element.range.unit
            )?.label || element.range.unit
          : availableUnits[element.setting.instance]?.find(
              (unit) => unit.value === element.setting.unit
            )?.label || element.setting.unit}
      </div>
      <div className="flex items-center gap-2">
        {hasRange && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <PencilIcon className="h-3 w-3 mr-1" />
            Слайдер
          </span>
        )}
        {hasSetting && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <EyeIcon className="h-3 w-3 mr-1" />
            Показатель
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(index)}
          className="p-1 text-gray-500 hover:text-blue-600 hover:bg-gray-100 transition-colors"
          title="Редактировать"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => onDelete(index)}
          className="p-1 text-gray-500 hover:text-red-600 hover:bg-gray-100 transition-colors"
          title="Удалить"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Компонент для формы добавления/редактирования
const FieldForm = ({
  settingsTranslations,
  selectedField,
  setSelectedField,
  selectedInstance,
  setSelectedInstance,
  selectedUnit,
  setSelectedUnit,
  selectedCapabilityType,
  setSelectedCapabilityType,
  rangeConfig,
  setRangeConfig,
  floatFields,
  floatSettings,
  getAvailableUnits,
  handleAddElement,
  editingIndex,
}) => {
  const isRange = selectedCapabilityType === 'range';

  return (
    <div className="bg-gray-50 p-4 border border-gray-200">
      <h4 className="text-base font-semibold text-gray-800 mb-3">
        {editingIndex !== null
          ? 'Редактирование поля'
          : 'Добавление нового поля'}
      </h4>

      <div className="space-y-4">
        {/* Basic Configuration */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Поле устройства
            </label>
            <select
              value={selectedField}
              onChange={(e) => setSelectedField(e.target.value)}
              className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">Выберите поле</option>
              {floatFields.map((field) => (
                <option key={field.name} value={field.name}>
                  {field.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Тип возможности
            </label>
            <select
              value={selectedCapabilityType}
              onChange={(e) => setSelectedCapabilityType(e.target.value)}
              className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="setting">Показатель</option>
              <option value="range">Слайдер</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Настройка
            </label>
            <select
              value={selectedInstance}
              onChange={(e) => setSelectedInstance(e.target.value)}
              className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              {floatSettings.map((setting) => (
                <option key={setting} value={setting}>
                  {settingsTranslations[setting]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Единица измерения
            </label>
            <select
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100"
              disabled={!selectedField || !selectedInstance}
            >
              {getAvailableUnits(selectedInstance).map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Range Configuration */}
        {isRange && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 p-4 bg-white border border-gray-200 rounded-lg">
            <h5 className="lg:col-span-4 text-sm font-semibold text-gray-700 mb-2">
              Конфигурация Range
            </h5>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Минимальное значение
              </label>
              <input
                type="number"
                step="0.1"
                value={rangeConfig.min}
                onChange={(e) =>
                  setRangeConfig((prev) => ({
                    ...prev,
                    min: parseFloat(e.target.value),
                  }))
                }
                className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Максимальное значение
              </label>
              <input
                type="number"
                step="0.1"
                value={rangeConfig.max}
                onChange={(e) =>
                  setRangeConfig((prev) => ({
                    ...prev,
                    max: parseFloat(e.target.value),
                  }))
                }
                className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Точность
              </label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={rangeConfig.precision}
                onChange={(e) =>
                  setRangeConfig((prev) => ({
                    ...prev,
                    precision: parseFloat(e.target.value),
                  }))
                }
                className="w-full border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Add/Update Button */}
        <div className="flex justify-end">
          <button
            onClick={handleAddElement}
            className={`px-6 py-2 text-white font-medium transition-colors ${
              editingIndex !== null
                ? 'bg-green-600 hover:bg-green-700'
                : !selectedField || !selectedInstance
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
            }`}
            disabled={!selectedField || !selectedInstance}
          >
            {editingIndex !== null ? 'Обновить' : 'Добавить'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Компонент для отображения таблицы
const FieldTable = ({
  elements,
  onEdit,
  onDelete,
  settingsTranslations,
  availableUnits,
}) => {
  return (
    <div className="space-y-2 px-4">
      <div className="bg-gray-50 p-2 border-b border-gray-200">
        <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-4 font-semibold text-gray-700 text-sm">
          <div>Поле из состояния устройства</div>
          <div>Настройка</div>
          <div>Единица измерения</div>
          <div>Тип возможности</div>
          <div>Действия</div>
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {elements.length > 0 ? (
          elements.map((element, index) => (
            <FieldRow
              key={index}
              element={element}
              index={index}
              onEdit={onEdit}
              onDelete={onDelete}
              settingsTranslations={settingsTranslations}
              availableUnits={availableUnits}
            />
          ))
        ) : (
          <div className="text-center py-8 bg-gray-50 border border-gray-200">
            <div className="text-gray-500 text-sm">Нет добавленных полей</div>
            <div className="text-gray-400 text-xs mt-1">
              Добавьте поля для настройки интеграции с Яндекс Умным домом
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function for range defaults
const getRangeDefaults = (instance) => {
  const defaults = {
    temperature: { min: 10, max: 30, precision: 0.5 },
    brightness: { min: 0, max: 100, precision: 1 },
    volume: { min: 0, max: 100, precision: 1 },
    humidity: { min: 0, max: 100, precision: 1 },
    channel: { min: 1, max: 999, precision: 1 },
    open: { min: 0, max: 100, precision: 1 },
  };

  return defaults[instance] || { min: 0, max: 100, precision: 1 };
};

function DeviceYandex({ closeModal, deviceId }) {
  const [elements, setElements] = useState([]);
  const [selectedField, setSelectedField] = useState('');
  const [selectedInstance, setSelectedInstance] = useState('temperature');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedCapabilityType, setSelectedCapabilityType] = useState('range');
  const [selectedDeviceType, setSelectedDeviceType] = useState(
    DEVICE_TYPES.SENSOR
  );
  const [rangeConfig, setRangeConfig] = useState({
    min: 0,
    max: 100,
    precision: 1,
  });
  const [editingIndex, setEditingIndex] = useState(null);

  const device = useSelector((state) =>
    state.device.devices.find((d) => d.id === deviceId)
  );

  useEffect(() => {
    if (device?.yandex_mapping) {
      const initialElements = device.yandex_mapping;

      // Set device type from first element if available
      if (initialElements.length > 0 && initialElements[0].device_type) {
        setSelectedDeviceType(initialElements[0].device_type);
      }

      setElements(initialElements);
    }
  }, [device]);

  useEffect(() => {
    const availableUnits = getAvailableUnits(selectedInstance);
    if (availableUnits.length > 0) {
      setSelectedUnit(availableUnits[0].value);
    }

    // Set range defaults when instance changes
    if (selectedCapabilityType === 'range') {
      setRangeConfig(getRangeDefaults(selectedInstance));
    }
  }, [selectedInstance, selectedCapabilityType]);

  const schema = useSelector((state) => state.device.schema);
  const floatFields = schema.filter(
    (field) => field.type === 'float' || field.type === 'int'
  );

  const settingsTranslations = {
    meter: 'Универсальный счетчик',
    amperage: 'Текущее потребление тока',
    battery_level: 'Уровень заряда аккумулятора',
    co2_level: 'Уровень углекислого газа',
    electricity_meter: 'Показания счетчика электроэнергии',
    food_level: 'Уровень корма',
    gas_meter: 'Показания счетчика газа',
    heat_meter: 'Показания счетчика тепла',
    humidity: 'Влажность',
    illumination: 'Уровень освещенности',
    pm1_density: 'Уровень загрязнения PM1',
    pm2_5_density: 'Уровень загрязнения PM2.5',
    pm10_density: 'Уровень загрязнения PM10',
    power: 'Потребляемая мощность',
    pressure: 'Давление',
    temperature: 'Температура',
    tvoc: 'Уровень загрязнения органическими веществами',
    voltage: 'Напряжение',
    water_level: 'Уровень воды',
    water_meter: 'Показания счетчика воды',
    brightness: 'Яркость',
    volume: 'Громкость',
    channel: 'Канал',
    open: 'Открытие',
  };

  const availableUnits = {
    meter: [{ value: '', label: '-' }],
    amperage: [{ value: 'unit.ampere', label: 'Ампер' }],
    battery_level: [{ value: 'unit.percent', label: 'Процент' }],
    co2_level: [{ value: 'unit.ppm', label: 'Миллионные доли' }],
    electricity_meter: [{ value: 'unit.kilowatt_hour', label: 'Киловатт-час' }],
    food_level: [{ value: 'unit.percent', label: 'Процент' }],
    gas_meter: [{ value: 'unit.cubic_meter', label: 'Кубический метр' }],
    heat_meter: [{ value: 'unit.gigacalorie', label: 'Гигакалории' }],
    humidity: [{ value: 'unit.percent', label: 'Процент' }],
    illumination: [{ value: 'unit.illumination.lux', label: 'Люкс' }],
    pm1_density: [{ value: 'unit.density.mcg_m3', label: 'мкг/м3' }],
    pm2_5_density: [{ value: 'unit.density.mcg_m3', label: 'мкг/м3' }],
    pm10_density: [{ value: 'unit.density.mcg_m3', label: 'мкг/м3' }],
    power: [{ value: 'unit.power.watt', label: 'Ватт' }],
    pressure: [
      { value: 'unit.pressure.atm', label: 'Атмосфер' },
      { value: 'unit.pressure.pascal', label: 'Паскаль' },
      { value: 'unit.pressure.bar', label: 'Бар' },
      { value: 'unit.pressure.mmhg', label: 'Миллиметров ртутного столба' },
    ],
    temperature: [
      { value: 'unit.temperature.celsius', label: 'Градусов Цельсия' },
      { value: 'unit.temperature.kelvin', label: 'Кельвин' },
    ],
    voltage: [
      { value: 'unit.voltage.volt', label: 'Вольт' },
      { value: 'unit.voltage.kilovolt', label: 'Киловольт' },
    ],
    tvoc: [{ value: 'unit.density.mcg_m3', label: 'мкг/м3' }],
    water_level: [{ value: 'unit.percent', label: 'Процент' }],
    water_meter: [{ value: 'unit.cubic_meter', label: 'Кубический метр' }],
    brightness: [{ value: 'unit.percent', label: 'Процент' }],
    volume: [{ value: 'unit.percent', label: 'Процент' }],
    channel: [{ value: '', label: '-' }],
    open: [{ value: 'unit.percent', label: 'Процент' }],
  };

  const isFieldDuplicate = (fieldName) => {
    return elements.some((element) => element.field === fieldName);
  };

  const handleAddElement = () => {
    if (selectedField && selectedInstance) {
      if (editingIndex === null && isFieldDuplicate(selectedField)) {
        toast.error('Поле уже добавлено в таблицу');
        return;
      }

      const newElement = {
        field: selectedField,
        device_type: selectedDeviceType,
        type: CAPABILITY_TYPES.RANGE,
      };

      if (selectedCapabilityType === 'range') {
        newElement.range = {
          instance: selectedInstance,
          unit: selectedUnit || null,
          random_access: true, // Всегда true, убрано из интерфейса
          range: {
            min: rangeConfig.min,
            max: rangeConfig.max,
            precision: rangeConfig.precision,
          },
        };
      } else {
        newElement.setting = {
          instance: selectedInstance,
          unit: selectedUnit || null,
        };
      }

      let updatedElements;
      if (editingIndex !== null) {
        updatedElements = [...elements];
        updatedElements[editingIndex] = newElement;
        setEditingIndex(null);
      } else {
        updatedElements = [...elements, newElement];
      }

      resetForm();
      const result = sendUpdateRequest(updatedElements);
      if (result === true) {
        setElements(updatedElements);
      }
    }
  };

  const handleEditElement = (index) => {
    const element = elements[index];
    setSelectedField(element.field);

    if (element.range) {
      setSelectedCapabilityType('range');
      setSelectedInstance(element.range.instance);
      setSelectedUnit(element.range.unit || '');
      setRangeConfig({
        min: element.range.range?.min || 0,
        max: element.range.range?.max || 100,
        precision: element.range.range?.precision || 1,
      });
    } else if (element.setting) {
      setSelectedCapabilityType('setting');
      setSelectedInstance(element.setting.instance);
      setSelectedUnit(element.setting.unit || '');
    }

    setEditingIndex(index);
  };

  const handleDeleteElement = (index) => {
    const updatedElements = elements.filter((_, i) => i !== index);
    const result = sendUpdateRequest(updatedElements);
    if (result === true) {
      setElements(updatedElements);
    }
  };

  const resetForm = () => {
    setSelectedField('');
    setSelectedInstance('temperature');
    setSelectedUnit('');
    setSelectedCapabilityType('range');
    setRangeConfig(getRangeDefaults('temperature'));
  };

  const sendUpdateRequest = async (updatedElements) => {
    try {
      await updateDevice(deviceId, { yandex_mapping: updatedElements });
      toast.success('Конфигурация Яндекс обновлена');
      return true;
    } catch (error) {
      console.error('Failed to update device:', error);
      toast.error('Ошибка при обновлении конфигурации');
      return false;
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const floatSettings = Object.keys(settingsTranslations);

  const getAvailableUnits = (setting) => {
    return availableUnits[setting] || [];
  };

  return (
    <ModalLayout closeModal={closeModal}>
      <div className="p-4 space-y-6" onClick={handleModalClick}>
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 pb-4 border-b border-gray-200">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Интеграция с приложением Дом с Алисой
            </h3>
          </div>
          <div className="bg-white p-2 border border-gray-200">
            <img
              className="w-40 bg-purple-950 h-auto"
              src={image}
              alt="Yandex Smart Home"
            />
          </div>
        </div>

        {/* Global Device Type Selection */}
        <div className="bg-white border border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <DevicePhoneMobileIcon className="h-5 w-5 text-gray-600" />
            <h4 className="text-lg font-semibold text-gray-900">
              Тип устройства
            </h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Выберите тип устройства для отображения в приложении Яндекс Умный
            дом
          </p>
          <select
            value={selectedDeviceType}
            onChange={(e) => setSelectedDeviceType(e.target.value)}
            className="w-full max-w-md border border-gray-300 p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            {Object.entries(DEVICE_TYPE_TRANSLATIONS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Table Section */}
        <div className="bg-white border border-gray-200">
          <FieldTable
            elements={elements}
            onEdit={handleEditElement}
            onDelete={handleDeleteElement}
            settingsTranslations={settingsTranslations}
            availableUnits={availableUnits}
          />
        </div>

        {/* Form Section */}
        <FieldForm
          settingsTranslations={settingsTranslations}
          selectedField={selectedField}
          setSelectedField={setSelectedField}
          selectedInstance={selectedInstance}
          setSelectedInstance={setSelectedInstance}
          selectedUnit={selectedUnit}
          setSelectedUnit={setSelectedUnit}
          selectedCapabilityType={selectedCapabilityType}
          setSelectedCapabilityType={setSelectedCapabilityType}
          rangeConfig={rangeConfig}
          setRangeConfig={setRangeConfig}
          floatFields={floatFields}
          floatSettings={floatSettings}
          getAvailableUnits={getAvailableUnits}
          handleAddElement={handleAddElement}
          editingIndex={editingIndex}
        />
      </div>
    </ModalLayout>
  );
}

export default DeviceYandex;
